package com.evoapps.evoshift.ui.tips

import com.evoapps.evoshift.util.usesDecimalComma
import com.evoapps.evoshift.domain.model.TipPool
import com.evoapps.evoshift.ui.finance.HoursExport
import com.evoapps.evoshift.ui.reports.ExportFile

/** The column titles of [TipsExport], in the app's language. */
data class TipExportLabels(
    val language: String,
    val from: String,
    val to: String,
    val employee: String,
    val role: String,
    val hours: String,
    val share: String
)

/**
 * SH-124: one pool's split as a CSV, one row per person: the period, the name, the role their
 * hours counted at, the hours (2 decimals) and the share. Amounts and hours use the language's
 * decimal mark. The same file conventions as [HoursExport]: ";" for Polish, "," for English,
 * UTF-8 with a BOM, CRLF, formulas defused. No tax columns: what tips mean for tax is for the
 * employer and their accountant.
 */
object TipsExport {
    fun fileName(workplaceId: String, pool: TipPool) = "evoshift-tips-${workplaceId}_${pool.id}.csv"

    fun file(workplaceId: String, pool: TipPool, labels: TipExportLabels): ExportFile =
        ExportFile(fileName(workplaceId, pool), HoursExport.MIME_TYPE, csv(pool, labels))

    fun csv(pool: TipPool, labels: TipExportLabels): String {
        val polish = usesDecimalComma(labels.language)
        val separator = if (polish) ";" else ","
        val header = listOf(labels.from, labels.to, labels.employee, labels.role, labels.hours, "${labels.share} (${pool.currency})")
        val lines = pool.shares.map { share ->
            listOf(
                pool.startDate,
                pool.endDate,
                share.displayName,
                share.role.orEmpty(),
                decimal(share.minutesWorked * 100 / 60, polish),
                decimal(share.amountMinor, polish)
            )
        }
        return BOM + (listOf(header) + lines).joinToString("") { row ->
            row.joinToString(separator) { HoursExport.cell(it, separator) } + "\r\n"
        }
    }

    /** [hundredths] as "12.50" (English) or "12,50" (Polish), without grouping. */
    private fun decimal(hundredths: Long, polish: Boolean): String =
        "${hundredths / 100}${if (polish) ',' else '.'}${(hundredths % 100).toString().padStart(2, '0')}"

    private const val BOM = "﻿"
}
